/**
 * WebpPlayer · 让动画 WebP 具备视频式播控 (play / pause / seek / currentTime)
 *
 * 做法:用 WebCodecs `ImageDecoder` API 逐帧解码 WebP → 每帧存 ImageBitmap +
 * 微秒级 duration → rAF 驱动时间轴 → 手动 drawImage 到 <canvas>。
 *
 * 优势:
 *   - 原生浏览器 API,不依赖 wasm polyfill
 *   - 单帧静态 WebP 自动降级为纯显示 (无控制条)
 *   - 不支持 ImageDecoder 或 fetch 因 CORS 拿不到时,降级到 <img> 展示动图
 *
 * 兼容:Chrome/Edge 94+, Safari 17+, Firefox 133+ (2026-08 状态)。
 *
 * 已知限制:
 *   - fetch(src) 会撞 BOS CORS,若目标桶未开 CORS 只能走 <img> 兜底 (无播控)
 *   - BOS 签名 URL 6h 过期;过期后需上游拉新 URL
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Slider, Space, Tag, Typography } from 'antd';
import {
  PlayCircleOutlined, PauseCircleOutlined, ReloadOutlined, StepBackwardOutlined, StepForwardOutlined,
} from '@ant-design/icons';

const { Text } = Typography;

interface DecodedFrame {
  bitmap: ImageBitmap;
  /** 帧展示时长 (ms);ImageDecoder 出的是微秒,内部已折算 */
  duration: number;
}

// ImageDecoder 类型在 TS lib 里未标注;这里做最小化 shape。
interface ImageDecoderFrame {
  image: {
    duration: number | null;  // µs;spec: microseconds
    displayWidth: number;
    displayHeight: number;
    close?: () => void;
  };
}
interface ImageDecoderCtor {
  new (init: { data: ArrayBuffer | Uint8Array; type: string }): {
    tracks: {
      ready: Promise<void>;
      selectedTrack: { frameCount: number };
    };
    decode: (opts: { frameIndex: number }) => Promise<ImageDecoderFrame>;
    close?: () => void;
  };
}

function hasImageDecoder(): boolean {
  return typeof (globalThis as unknown as { ImageDecoder?: ImageDecoderCtor }).ImageDecoder === 'function';
}

export interface WebpPlayerProps {
  src: string;
  alt?: string;
  /** 画面最大高度 (px);canvas 会等比缩放。 */
  maxHeight?: number;
  /** 首屏是否自动播放,默认 true。 */
  autoPlay?: boolean;
  /** 解码失败 (含 404/CORS/不支持 ImageDecoder) 时通知父组件;父组件可选择整块隐藏。
   *  未提供时组件内部会 fallback 到 <img> 展示。 */
  onError?: (msg: string) => void;
  /** 解码失败时是否隐藏组件自身,配合 onError 使用。默认 false (走 <img> 降级)。 */
  hideOnError?: boolean;
}

export default function WebpPlayer({
  src, alt, maxHeight = 320, autoPlay = true, onError, hideOnError = false,
}: WebpPlayerProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [frames, setFrames] = useState<DecodedFrame[] | null>(null);
  const [decodeErr, setDecodeErr] = useState<string | null>(null);
  const [decoding, setDecoding] = useState(true);
  const [playing, setPlaying] = useState(autoPlay);
  /** 时间线累计 (ms);seek 时直接写入,rAF 累加。 */
  const [elapsedMs, setElapsedMs] = useState(0);

  const totalMs = useMemo(
    () => (frames ? frames.reduce((a, f) => a + f.duration, 0) : 0),
    [frames],
  );

  // 计算当前帧下标 (基于 elapsedMs)。
  const currentIdx = useMemo(() => {
    if (!frames || frames.length === 0) return 0;
    let acc = 0;
    for (let i = 0; i < frames.length; i++) {
      acc += frames[i].duration;
      if (elapsedMs < acc) return i;
    }
    return frames.length - 1;
  }, [frames, elapsedMs]);

  // ── 解码 WebP → 帧列表 ─────────────────────────
  useEffect(() => {
    let alive = true;
    setDecoding(true);
    setDecodeErr(null);
    setFrames(null);
    setElapsedMs(0);
    (async () => {
      try {
        if (!hasImageDecoder()) {
          throw new Error('浏览器不支持 ImageDecoder,已降级到静态图片');
        }
        const resp = await fetch(src, { credentials: 'omit' });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const buf = await resp.arrayBuffer();

        const Ctor = (globalThis as unknown as { ImageDecoder: ImageDecoderCtor }).ImageDecoder;
        const decoder = new Ctor({ data: buf, type: 'image/webp' });
        await decoder.tracks.ready;
        const track = decoder.tracks.selectedTrack;
        const frameCount = track.frameCount;

        const out: DecodedFrame[] = [];
        for (let i = 0; i < frameCount; i++) {
          const { image } = await decoder.decode({ frameIndex: i });
          // WebCodecs VideoFrame.duration 单位是微秒;缺省时(单帧静态) fallback 100ms
          const durMs = image.duration ? Math.max(1, Math.round(image.duration / 1000)) : 100;
          // ImageBitmap 拷贝可脱离 VideoFrame 生命周期,VideoFrame close 后仍可用
          const bitmap = await createImageBitmap(image as unknown as ImageBitmapSource);
          image.close?.();
          if (!alive) {
            bitmap.close?.();
            return;
          }
          out.push({ bitmap, duration: durMs });
        }
        decoder.close?.();
        if (alive) setFrames(out);
      } catch (e) {
        if (!alive) return;
        const msg = (e as Error).message || '解码失败';
        setDecodeErr(msg);
        onError?.(msg);
      } finally {
        if (alive) setDecoding(false);
      }
    })();
    return () => {
      alive = false;
    };
    // onError 仅在 mount 时读一次即可,不放依赖避免 setState 循环触发重解码
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  // 组件卸载 or src 变化时清 ImageBitmap (避免 GPU 内存泄漏)
  useEffect(() => {
    return () => {
      frames?.forEach(f => f.bitmap.close?.());
    };
    // 只在 frames 引用换新或卸载时清旧的一份
  }, [frames]);

  // ── rAF 驱动时间累加 ─────────────────────────
  useEffect(() => {
    if (!playing || !frames || frames.length <= 1 || totalMs === 0) return;
    let raf = 0;
    let last = performance.now();
    const tick = (now: number) => {
      const dt = now - last;
      last = now;
      setElapsedMs(prev => {
        const next = prev + dt;
        // 循环播放
        return next >= totalMs ? next % totalMs : next;
      });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing, frames, totalMs]);

  // ── 把当前帧画到 canvas ─────────────────────
  useEffect(() => {
    const cvs = canvasRef.current;
    if (!cvs || !frames || frames.length === 0) return;
    const f = frames[currentIdx];
    if (!f) return;
    if (cvs.width !== f.bitmap.width) cvs.width = f.bitmap.width;
    if (cvs.height !== f.bitmap.height) cvs.height = f.bitmap.height;
    const ctx = cvs.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(f.bitmap, 0, 0);
  }, [frames, currentIdx]);

  // ── 渲染 ───────────────────────────────────
  if (decoding) {
    return (
      <div style={{ padding: 20, textAlign: 'center', color: '#8c8c8c', fontSize: 12 }}>
        解码中…
      </div>
    );
  }

  // 解码失败 / 不支持:降级到 <img>,让 BOS 直接给动图播放 (无播控);
  // hideOnError=true 时直接不渲染,由父组件靠 onError 决定是否隐藏整块
  if (decodeErr || !frames || frames.length === 0) {
    if (hideOnError) return null;
    return (
      <div>
        <img
          src={src}
          alt={alt}
          style={{ maxHeight, maxWidth: '100%', objectFit: 'contain', display: 'block', margin: '0 auto' }}
        />
        {decodeErr && (
          <Alert
            type="info"
            showIcon
            message={`降级为静态展示: ${decodeErr}`}
            style={{ marginTop: 6, fontSize: 11 }}
          />
        )}
      </div>
    );
  }

  const isStatic = frames.length <= 1;
  const currentMs = Math.min(elapsedMs, totalMs);
  const remainMs = Math.max(0, totalMs - currentMs);

  const seekTo = (ms: number) => {
    setElapsedMs(Math.max(0, Math.min(totalMs, ms)));
  };
  const seekFrame = (delta: number) => {
    if (!frames) return;
    setPlaying(false);
    const nextIdx = Math.max(0, Math.min(frames.length - 1, currentIdx + delta));
    let acc = 0;
    for (let i = 0; i < nextIdx; i++) acc += frames[i].duration;
    setElapsedMs(acc);
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        style={{
          maxHeight,
          maxWidth: '100%',
          objectFit: 'contain',
          display: 'block',
          margin: '0 auto',
          cursor: isStatic ? 'default' : 'pointer',
          background: '#000',
        }}
        onClick={() => !isStatic && setPlaying(p => !p)}
      />

      {!isStatic && (
        <div style={{ marginTop: 8 }}>
          <Slider
            min={0}
            max={totalMs}
            step={10}
            value={currentMs}
            tooltip={{ formatter: v => `${((v ?? 0) / 1000).toFixed(2)}s` }}
            onChange={v => {
              setPlaying(false);
              seekTo(v as number);
            }}
          />
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, flexWrap: 'wrap',
          }}>
            <Space size={4}>
              <Button
                size="small"
                icon={playing ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                onClick={() => setPlaying(p => !p)}
              >
                {playing ? '暂停' : '播放'}
              </Button>
              <Button
                size="small"
                icon={<StepBackwardOutlined />}
                onClick={() => seekFrame(-1)}
                title="上一帧"
              />
              <Button
                size="small"
                icon={<StepForwardOutlined />}
                onClick={() => seekFrame(1)}
                title="下一帧"
              />
              <Button
                size="small"
                icon={<ReloadOutlined />}
                onClick={() => { setElapsedMs(0); setPlaying(true); }}
                title="重播"
              />
            </Space>

            <div style={{ flex: 1 }} />

            <Text style={{ fontSize: 12, fontFamily: 'ui-monospace, Menlo, Consolas, monospace' }}>
              {(currentMs / 1000).toFixed(2)}s
              <Text type="secondary"> / {(totalMs / 1000).toFixed(2)}s</Text>
              <Text type="secondary"> · -{(remainMs / 1000).toFixed(2)}s</Text>
            </Text>

            <Tag color="default" style={{ margin: 0 }}>
              帧 {currentIdx + 1} / {frames.length}
            </Tag>
          </div>
        </div>
      )}
    </div>
  );
}
